import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Modal } from '@components/molecules/Modal';
import { Button } from '@components/atoms/Button';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { WarehouseOption } from '@components/molecules/WarehouseSelector';
import { MaterialSelector, MaterialOption } from '@components/molecules/MaterialSelector';
import {
  MaterialFromCalculator,
  CalculatorType,
  CALCULATOR_CATEGORY_MAP,
} from '@components/calculators/types/calculator.types';
import {
  useWarehouses,
  useMaterialCatalog,
  useMaterialCatalogByCalculatorType,
  useCreateTaskMaterialFromWarehouse,
  useStockLevels,
} from '@hooks/api/warehouse';
import {
  buildAvailabilityIndex,
  describeStockElsewhere,
  getCatalogAvailability,
} from '@utils/stockAvailability';
import { useCreateMaterial } from '@hooks/api/material/useCreateMaterial';
import { StockLevel } from '@models/warehouse.model';
import { warehouseService } from '@services/warehouse';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import EditIcon from '@mui/icons-material/Edit';

type RowAction = 'match' | 'skip';
type SourceType = 'manual' | 'warehouse';

interface SourceOption extends SelectOption {
  sourceType: SourceType;
  warehouse?: WarehouseOption;
}

interface RowState {
  action: RowAction;
  sourceType: SourceType | null;
  selectedWarehouse: WarehouseOption | null;
  selectedMaterial: MaterialOption | null;
  isProcessed: boolean;
  isProcessing: boolean;
}

interface CalculatorMatchingModalProps {
  isOpen: boolean;
  onClose: () => void;
  calculatorResults: MaterialFromCalculator[];
  calculatorType?: CalculatorType;
  taskId: string;
  onComplete?: () => void;
}

const MANUAL_SOURCE_VALUE = '__manual__';

export const CalculatorMatchingModal: React.FC<CalculatorMatchingModalProps> = ({
  isOpen,
  onClose,
  calculatorResults,
  calculatorType,
  taskId,
  onComplete,
}) => {
  const [rowStates, setRowStates] = useState<RowState[]>([]);
  const [isProcessingAll, setIsProcessingAll] = useState(false);
  const [showAllMaterials, setShowAllMaterials] = useState(false);
  const [stockLevelsCache, setStockLevelsCache] = useState<Record<string, StockLevel[]>>({});
  const [stockLoadingWarehouses, setStockLoadingWarehouses] = useState<Set<string>>(new Set());

  // Data hooks
  const { data: warehouses = [], isLoading: warehousesLoading } = useWarehouses();
  const { data: allMaterials = [], isLoading: allMaterialsLoading } = useMaterialCatalog();
  // Org-wide stock, used only to explain where a material actually lives when
  // the row's warehouse has none. Rows stay warehouse-scoped for reservation.
  const { data: allStockLevels = [] } = useStockLevels();
  const { data: categoryMaterials = [], isLoading: categoryMaterialsLoading } =
    useMaterialCatalogByCalculatorType(calculatorType);

  const availabilityIndex = useMemo(
    () => buildAvailabilityIndex(allStockLevels, warehouses),
    [allStockLevels, warehouses],
  );

  // Determine which materials to show
  const materials = showAllMaterials || !calculatorType ? allMaterials : categoryMaterials;
  const materialsLoading = showAllMaterials || !calculatorType ? allMaterialsLoading : categoryMaterialsLoading;

  // Category label for display
  const categoryLabel = calculatorType ? CALCULATOR_CATEGORY_MAP[calculatorType]?.categoryName : undefined;

  // Mutations
  const createTaskMaterial = useCreateTaskMaterialFromWarehouse();
  const { createMaterial: createManualMaterial } = useCreateMaterial();

  // Build source options: Manual + warehouses
  const sourceOptions: SourceOption[] = useMemo(() => {
    const manualOption: SourceOption = {
      value: MANUAL_SOURCE_VALUE,
      label: 'Manual',
      sourceType: 'manual',
    };

    const warehouseOptions: SourceOption[] = warehouses
      .filter((w) => w.isActive)
      .map((w) => ({
        value: w.warehouseId,
        label: w.name,
        sourceType: 'warehouse' as SourceType,
        warehouse: {
          value: w.warehouseId,
          label: w.name,
          warehouse: w,
        },
      }));

    return [manualOption, ...warehouseOptions];
  }, [warehouses]);

  // Custom option renderer for source selector
  const formatSourceOptionLabel = useCallback(
    (option: SourceOption, { context }: { context: 'menu' | 'value' }) => {
      if (option.sourceType === 'manual') {
        if (context === 'value') {
          return (
            <div className="flex items-center gap-2">
              <EditIcon className="text-gray-500" sx={{ fontSize: 16 }} />
              <span className="truncate">Manual</span>
            </div>
          );
        }
        return (
          <div className="flex items-center gap-3 w-full py-0.5">
            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <EditIcon className="text-gray-500" sx={{ fontSize: 16 }} />
            </div>
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-sm font-medium">Manual</span>
              <span className="text-xs text-gray-500">Add without cost — edit later</span>
            </div>
          </div>
        );
      }

      // Warehouse option
      const warehouse = option.warehouse?.warehouse;
      if (context === 'value') {
        return (
          <div className="flex items-center gap-2">
            <WarehouseIcon className="text-indigo-500" sx={{ fontSize: 16 }} />
            <span className="truncate">{warehouse?.name || option.label}</span>
            {warehouse?.isDefault && (
              <span className="px-1.5 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded">
                Default
              </span>
            )}
          </div>
        );
      }

      return (
        <div className="flex items-center gap-3 w-full py-0.5">
          <div className="w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center flex-shrink-0">
            <WarehouseIcon className="text-indigo-500" sx={{ fontSize: 16 }} />
          </div>
          <div className="flex flex-col min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium truncate">
                {warehouse?.name || option.label}
              </span>
              {warehouse?.isDefault && (
                <span className="px-1.5 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded">
                  Default
                </span>
              )}
            </div>
            {warehouse?.address && (
              <span className="text-xs text-gray-500 truncate">{warehouse.address}</span>
            )}
          </div>
        </div>
      );
    },
    [],
  );

  // Initialize row states from calculator results
  useEffect(() => {
    if (isOpen && calculatorResults.length > 0) {
      setRowStates(
        calculatorResults.map(() => ({
          action: 'match' as RowAction,
          sourceType: null,
          selectedWarehouse: null,
          selectedMaterial: null,
          isProcessed: false,
          isProcessing: false,
        })),
      );
    }
  }, [isOpen, calculatorResults]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setRowStates([]);
      setIsProcessingAll(false);
      setShowAllMaterials(false);
      setStockLevelsCache({});
      setStockLoadingWarehouses(new Set());
    }
  }, [isOpen]);

  // Ref to track in-flight fetches and avoid duplicates
  const fetchingRef = useRef<Set<string>>(new Set());

  // Collect unique warehouse IDs that need stock levels
  const neededWarehouseIds = useMemo(() => {
    const ids: string[] = [];
    rowStates.forEach((row) => {
      const wId = row.selectedWarehouse?.warehouse.warehouseId;
      if (row.sourceType === 'warehouse' && wId && !stockLevelsCache[wId]) {
        ids.push(wId);
      }
    });
    return ids;
  }, [rowStates, stockLevelsCache]);

  // Reactively fetch stock levels when new warehouses are selected
  useEffect(() => {
    neededWarehouseIds.forEach((warehouseId) => {
      if (fetchingRef.current.has(warehouseId)) return;
      fetchingRef.current.add(warehouseId);
      setStockLoadingWarehouses((prev) => new Set(prev).add(warehouseId));

      warehouseService
        .getStockLevels(warehouseId)
        .then((levels) => {
          setStockLevelsCache((prev) => ({ ...prev, [warehouseId]: levels || [] }));
        })
        .catch(() => {
          setStockLevelsCache((prev) => ({ ...prev, [warehouseId]: [] }));
        })
        .finally(() => {
          fetchingRef.current.delete(warehouseId);
          setStockLoadingWarehouses((prev) => {
            const next = new Set(prev);
            next.delete(warehouseId);
            return next;
          });
        });
    });
  }, [neededWarehouseIds]);

  // Helper: get stock info for a material in a specific warehouse
  const getStockForMaterial = useCallback(
    (catalogId: string, warehouseId: string) => {
      const levels = stockLevelsCache[warehouseId] || [];
      return levels.find((sl) => sl.catalogId === catalogId);
    },
    [stockLevelsCache],
  );

  const updateRowState = useCallback((index: number, updates: Partial<RowState>) => {
    setRowStates((prev) => {
      const newStates = [...prev];
      newStates[index] = { ...newStates[index], ...updates };
      return newStates;
    });
  }, []);

  // Handle source change for a row
  const handleSourceChange = useCallback(
    (index: number, selected: any) => {
      if (!selected) {
        updateRowState(index, {
          sourceType: null,
          selectedWarehouse: null,
          selectedMaterial: null,
        });
        return;
      }

      const option = sourceOptions.find((opt) => opt.value === selected.value);
      if (!option) return;

      if (option.sourceType === 'manual') {
        updateRowState(index, {
          sourceType: 'manual',
          selectedWarehouse: null,
          selectedMaterial: null,
        });
      } else {
        updateRowState(index, {
          sourceType: 'warehouse',
          selectedWarehouse: option.warehouse || null,
          selectedMaterial: null,
        });
        // Stock levels are fetched reactively via useEffect
      }
    },
    [sourceOptions, updateRowState],
  );

  // Get the current source option for a row
  const getSourceValue = useCallback(
    (row: RowState): SourceOption | null => {
      if (!row.sourceType) return null;
      if (row.sourceType === 'manual') {
        return sourceOptions.find((o) => o.value === MANUAL_SOURCE_VALUE) || null;
      }
      if (row.selectedWarehouse) {
        return sourceOptions.find((o) => o.value === row.selectedWarehouse?.warehouse.warehouseId) || null;
      }
      return null;
    },
    [sourceOptions],
  );

  // Process a single row
  const processRow = async (index: number) => {
    const row = rowStates[index];
    const calcResult = calculatorResults[index];
    if (row.isProcessed) return;

    updateRowState(index, { isProcessing: true });

    try {
      if (row.action === 'skip') {
        updateRowState(index, { isProcessed: true, isProcessing: false });
        return;
      }

      // Auto-skip rows without a source selected
      if (!row.sourceType) {
        updateRowState(index, { action: 'skip', isProcessed: true, isProcessing: false });
        return;
      }

      if (row.sourceType === 'manual') {
        // Create manual material (no cost, user edits later)
        await createManualMaterial({
          name: calcResult.suggestedName || calcResult.resultLabel,
          supplier: '',
          time: new Date().toISOString(),
          amount: calcResult.amount,
          costPerUnit: 0,
          amountType: calcResult.amountType,
          amountSystem: calcResult.amountSystem,
          taskId,
          chargeToClient: false,
        });
        updateRowState(index, { isProcessed: true, isProcessing: false });
      } else if (row.sourceType === 'warehouse') {
        // Auto-skip warehouse rows without a material selected
        if (!row.selectedMaterial || !row.selectedWarehouse) {
          updateRowState(index, { action: 'skip', isProcessed: true, isProcessing: false });
          return;
        }

        // Create task material from warehouse
        await createTaskMaterial.mutateAsync({
          taskId,
          catalogId: row.selectedMaterial.material.catalogId,
          warehouseId: row.selectedWarehouse.warehouse.warehouseId,
          quantity: calcResult.amount,
        });
        updateRowState(index, { isProcessed: true, isProcessing: false });
      }
    } catch (error) {
      updateRowState(index, { isProcessing: false });
    }
  };

  // Process all non-skipped rows
  const processAll = async () => {
    setIsProcessingAll(true);
    for (let i = 0; i < rowStates.length; i++) {
      if (!rowStates[i].isProcessed) {
        await processRow(i);
      }
    }
    setIsProcessingAll(false);
    onComplete?.();
    onClose();
  };

  const allProcessed = rowStates.length > 0 && rowStates.every((r) => r.isProcessed);
  const hasUnprocessed = rowStates.some((r) => !r.isProcessed);

  // Count summary
  const readyCount = rowStates.filter((r) => {
    if (r.isProcessed || r.action === 'skip') return false;
    if (r.sourceType === 'manual') return true;
    if (r.sourceType === 'warehouse' && r.selectedMaterial) return true;
    return false;
  }).length;
  const skipCount = rowStates.filter((r) => r.action === 'skip' && !r.isProcessed).length;
  const pendingCount = rowStates.filter((r) => {
    if (r.isProcessed || r.action === 'skip') return false;
    if (!r.sourceType) return true;
    if (r.sourceType === 'warehouse' && !r.selectedMaterial) return true;
    return false;
  }).length;
  const processedCount = rowStates.filter((r) => r.isProcessed).length;

  const canProcess = hasUnprocessed && !isProcessingAll && readyCount > 0;

  // Check if any warehouse row exists (to show/hide category filter toggle)
  const hasWarehouseRows = rowStates.some((r) => r.sourceType === 'warehouse' && r.action !== 'skip');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Calculator Results"
      confirmText={allProcessed ? 'Done' : `Process All (${readyCount} items)`}
      handleConfirm={allProcessed ? onClose : processAll}
      isLoading={isProcessingAll}
      disableConfirm={!allProcessed && !canProcess}
      size="large">
      <div className="flex flex-col gap-5">
        {/* Category filter info + toggle — only visible when at least one warehouse source is selected */}
        {calculatorType && hasWarehouseRows && (
          <div className="flex items-center justify-between px-1">
            <span className="text-sm text-gray-500">
              {showAllMaterials
                ? `Showing all materials (${allMaterials.length})`
                : `Showing ${categoryMaterials.length} ${categoryLabel || ''} materials`}
            </span>
            <Button
              variant="tertiary"
              onClick={() => setShowAllMaterials(!showAllMaterials)}
              type="button">
              {showAllMaterials ? 'Show category only' : 'Search all materials'}
            </Button>
          </div>
        )}

        {/* Progress summary */}
        {rowStates.length > 0 && (
          <div className="flex items-center gap-4 text-sm text-gray-600 px-1">
            <span>{processedCount}/{rowStates.length} processed</span>
            {readyCount > 0 && <span className="text-green-600">{readyCount} ready</span>}
            {pendingCount > 0 && <span className="text-amber-500">{pendingCount} unmatched</span>}
            {skipCount > 0 && <span className="text-gray-400">{skipCount} skipped</span>}
          </div>
        )}

        {/* No category materials warning */}
        {calculatorType && hasWarehouseRows && !showAllMaterials && categoryMaterials.length === 0 && !categoryMaterialsLoading && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
            <WarningAmberIcon sx={{ fontSize: 18 }} />
            <span>
              No materials found in the <strong>{categoryLabel}</strong> category.
              You can{' '}
              <button
                className="underline font-medium"
                onClick={() => setShowAllMaterials(true)}
                type="button">
                search all materials
              </button>{' '}
              instead.
            </span>
          </div>
        )}

        {/* Calculator result rows */}
        <div className="space-y-4">
          {calculatorResults.map((result, index) => {
            const row = rowStates[index];
            if (!row) return null;
            // Stock info for warehouse rows
            const rowWarehouseId = row.selectedWarehouse?.warehouse.warehouseId;
            const isStockLoading = rowWarehouseId ? stockLoadingWarehouses.has(rowWarehouseId) : false;
            const rowStockLevels = rowWarehouseId ? (stockLevelsCache[rowWarehouseId] || []) : [];
            const selectedStock = row.selectedMaterial && rowWarehouseId
              ? getStockForMaterial(row.selectedMaterial.material.catalogId, rowWarehouseId)
              : null;
            const isOutOfStock =
              row.selectedMaterial &&
              selectedStock &&
              (selectedStock.quantityOnHand - selectedStock.quantityReserved) <= 0;
            const hasNoStockRecord = row.selectedMaterial && rowWarehouseId && !selectedStock;
            // Zero here doesn't mean zero everywhere — name the warehouses that
            // actually hold it so the row isn't mistaken for "we're out".
            const rowStockElsewhere = row.selectedMaterial
              ? describeStockElsewhere(
                  getCatalogAvailability(
                    availabilityIndex,
                    row.selectedMaterial.material.catalogId,
                  ),
                  rowWarehouseId,
                )
              : '';

            return (
              <div
                key={index}
                className={`border rounded-lg p-4 ${
                  row.isProcessed
                    ? 'border-green-200 bg-green-50'
                    : 'border-gray-200 bg-white'
                }`}>
                {/* Calculator result header */}
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {result.suggestedName || result.resultLabel}
                    </p>
                    <p className="text-xs text-gray-500">
                      {result.amount} {result.amountType} · {result.amountSystem}
                    </p>
                  </div>
                  {row.isProcessed && (
                    <CheckCircleOutlineIcon className="text-green-500" sx={{ fontSize: 20 }} />
                  )}
                </div>

                {/* Row content */}
                {!row.isProcessed && (
                  <>
                    {row.action === 'skip' ? (
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-400 italic">
                          This item will be skipped and not added to the task.
                        </p>
                        <Button
                          variant="tertiary"
                          onClick={() => updateRowState(index, { action: 'match', sourceType: null, selectedWarehouse: null, selectedMaterial: null })}
                          type="button">
                          Undo
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-3">
                        {/* Source selector + Skip button */}
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <ReactSelect
                              options={sourceOptions}
                              value={getSourceValue(row)}
                              onChange={(selected: any) => handleSourceChange(index, selected)}
                              placeholder="Select source..."
                              isLoading={warehousesLoading}
                              isSearchable
                              isClearable
                              formatOptionLabel={formatSourceOptionLabel as any}
                            />
                          </div>
                          <Button
                            variant="tertiary"
                            leftIcon={<SkipNextIcon sx={{ fontSize: 16 }} />}
                            onClick={() => updateRowState(index, { action: 'skip' })}
                            type="button">
                            Skip
                          </Button>
                        </div>

                        {/* Manual source info */}
                        {row.sourceType === 'manual' && (
                          <div className="flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600">
                            <EditIcon sx={{ fontSize: 14 }} />
                            <span>
                              Material will be added with calculated quantity. You can edit the cost and details later.
                            </span>
                          </div>
                        )}

                        {/* Material selector — only for warehouse source */}
                        {row.sourceType === 'warehouse' && row.selectedWarehouse && (() => {
                          // Filter materials to only those stocked in the selected warehouse
                          const warehouseCatalogIds = new Set(
                            rowStockLevels.map((sl) => sl.catalogId),
                          );
                          // Don't show unfiltered materials while stock levels are loading
                          const warehouseMaterials = isStockLoading
                            ? []
                            : warehouseCatalogIds.size > 0
                              ? materials.filter((m) => warehouseCatalogIds.has(m.catalogId))
                              : materials;
                          // Always show full warehouse materials list, not just auto-matches
                          const displayMaterials = warehouseMaterials;

                          return (
                          <>
                            <MaterialSelector
                              materials={displayMaterials}
                              stockLevels={rowStockLevels}
                              value={row.selectedMaterial}
                              onChange={(option) => updateRowState(index, { selectedMaterial: option })}
                              warehouseId={rowWarehouseId}
                              isLoading={materialsLoading || isStockLoading}
                              showOutOfStock
                              allowOutOfStock
                              placeholder={`Search ${row.selectedWarehouse.warehouse.name} materials...`}
                            />

                            {/* No stock warning */}
                            {(isOutOfStock || hasNoStockRecord) && (
                              <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                                <WarningAmberIcon sx={{ fontSize: 14 }} />
                                <span>
                                  No stock available in {row.selectedWarehouse.warehouse.name}.
                                  {rowStockElsewhere
                                    ? ' '
                                    : ' The material will be added as '}
                                  {rowStockElsewhere ? (
                                    <>
                                      Available elsewhere: <strong>{rowStockElsewhere}</strong>.
                                      Switch warehouse to reserve it, or keep it as{' '}
                                      <strong>Planned</strong>.
                                    </>
                                  ) : (
                                    <>
                                      <strong>Planned</strong> and may need to be ordered.
                                    </>
                                  )}
                                </span>
                              </div>
                            )}

                            {/* Stock info when material is selected and has stock */}
                            {selectedStock && !isOutOfStock && (
                              <p className="text-xs text-green-600 px-1">
                                Available: {(selectedStock.quantityOnHand - selectedStock.quantityReserved).toFixed(2)} in stock
                              </p>
                            )}
                          </>
                          );
                        })()}
                      </div>
                    )}
                  </>
                )}

                {/* Processing indicator */}
                {row.isProcessing && (
                  <p className="text-xs text-indigo-600 animate-pulse">Processing...</p>
                )}
              </div>
            );
          })}
        </div>

        {/* Empty state */}
        {calculatorResults.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>No calculator results to match.</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CalculatorMatchingModal;
